import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Message, { MessageVariant } from 'components/Message/Message';
import { compareCzechDates, formatAmountValue, sqlDateToLabel } from 'helpers/helpers';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import AccountComplete from 'pages/Accounts/Complete/AccountComplete';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { RootStore } from 'stores/rootStore';
import useStyles from '../styles';
import { AccountMode, ActiveStatus, Request, SignatureFlow, TaxMode } from 'enums';
import { DetailPage } from '../enums';
import { PendingChanges } from '../../../../stores/account/model';

interface Props {
  rootStore?: RootStore;
  store: AccountDetailStore;
  iqOverdue: boolean;
  userMode: boolean;
}

const Notifications: React.FC<Props> = ({ store, iqOverdue, rootStore, userMode }) => {
  const { t } = useTranslation();
  const classes = useStyles({ userMode: false });
  const [requestOpen, setRequestOpen] = useState(false);

  const now = new Date();
  const iqPast =
    store.data.detail?.investmentQuestionnaireValidityDate?.date &&
    iqOverdue &&
    DateTime.fromFormat(store.data.detail.investmentQuestionnaireValidityDate.date, 'yyyy-MM-dd') <
      DateTime.fromJSDate(now);
  const extMsg =
    store.data.summary?.accountsSummary.some((account) => account.activeStatus !== ActiveStatus.CLOSED) &&
    store.activeStatus === ActiveStatus.CLOSED
      ? '_ext'
      : '';
  const inTermination = store.activeStatus === ActiveStatus.CLOSED && store.canTerminateContract === false;

  const pendingTransfers = store.data.detail?.pendingTransfer
    .filter((pt) => pt.target)
    .map((pt) => ({ ...pt, date: sqlDateToLabel(pt.date, 'd. M. yyyy') }))
    .sort((a, b) => compareCzechDates(a.date, b.date));

  return (
    <div className={classes.notifications}>
      {/* EDS-4915 */}
      {!userMode && store.data.detail && store.taxMode !== 'PPP' && (
        <Message
          id="eds4915-2"
          message={
            'Klient ještě nepodepsal výzvu k podpisu dodatku Rámcové smlouvy na změnu režimu z Obhospodařování na Přijímání a předávání pokynů.'
          }
          tooltip={
            'Aby klient mohl i po 1. 1. 2025 využívat všechny služby Edwarda a zároveň platil menší poplatky za vedení účtu než doposud, je důležité, aby klient dodatek Rámcové smlouvy podepsal.'
          }
          variant={MessageVariant.INFO}
          action={() => setRequestOpen(true)}
          actionText={'Odeslat výzvu klientovi'}
          actionIcon={false}
          permanent
        />
      )}
      {/* EDS-4915 */}
      <ConfirmDialog
        noHeader
        isOpen={requestOpen}
        confirm={() => {
          setRequestOpen(false);
          store.resendAmendmentRequest();
        }}
        close={() => setRequestOpen(false)}
      >
        Opravdu chcete odeslat výzvu klientovi?
      </ConfirmDialog>

      {store.data.detail && iqOverdue && !userMode && (
        <Message
          id={`IQvalidityDate_${store.data.detail.accountId}`}
          message={`${t('accountDetail_questionnaireValidity')}${t(
            iqPast ? 'accountDetail_questionnaireOverduePast' : 'accountDetail_questionnaireOverdue',
            {
              date: sqlDateToLabel(store.data.detail.investmentQuestionnaireValidityDate.date, 'dd. MM. yyyy'),
            },
          )}${t('accountDetail_questionnaireUpdate')}`}
          variant={MessageVariant.WARNING}
          userStore={rootStore!.stores.userStore}
        />
      )}

      {store.data.detail && store.riskProfileMismatch && !userMode && (
        <Message
          id={`account_${store.data.detail.accountId}`}
          message={t('accountDetail_riskProfileMismatch')}
          variant={MessageVariant.WARNING}
          permanent
        />
      )}

      {store.pendingSignature && !userMode && (
        <Message
          id="account_pendingSignature"
          message={t(
            `accountDetail_pending_signature${store.pendingChanges === PendingChanges.PORTFOLIO ? '_PORTFOLIO' : ''}`,
          )}
          variant={MessageVariant.INFO}
          permanent
          action={store.cancel}
          actionText={t('accountDetail_cancelEdits')}
        />
      )}
      {store.pendingRequests.length > 0 && !userMode && (
        <Message
          id="account_pendingRequest"
          message={t(inTermination ? 'accountDetail_pending_terminationRequest' : 'accountDetail_pending_request')}
          variant={MessageVariant.INFO}
          permanent
          action={store.showConfirmRequest}
          actionText={t('accountDetail_cancel_CANCEL_PROCESSING')}
        />
      )}

      {store.mode !== AccountMode.LTIP && store.pendingWood && !store.activeStatus && (
        <Message
          id="account_pendingWood"
          message={t('accountDetail_pending_wood')}
          variant={MessageVariant.INFO}
          permanent
        />
      )}

      {store.mode === AccountMode.LTIP && store.pendingWood && !store.activeStatus && (
        <Message
          id="account_pendingWood"
          message={t('accountDetail_pending_wood_LTIP')}
          variant={MessageVariant.INFO}
          permanent
        />
      )}

      {store.activeStatus &&
        !store.canTerminateContract &&
        [
          ActiveStatus.LOCKED,
          ActiveStatus.IN_LIQUIDATION,
          ActiveStatus.CLOSED,
          ActiveStatus.IN_TERMINATION,
          ActiveStatus.CLIENT_DECEASED,
        ].includes(store.activeStatus) && (
          <Message
            id="account_activeStatus"
            message={t(`tooltip_activeStatus_${store.activeStatus}${extMsg}`)}
            variant={MessageVariant.INFO}
            permanent
          />
        )}

      {store.data.detail?.pendingWithdrawal &&
        store.data.detail.pendingWithdrawal?.length > 0 &&
        store.data.detail.pendingWithdrawal.map((withdrawal) => (
          <Message
            key={`account_pendingWithdrawal_${withdrawal.type}`}
            id={`account_pendingWithdrawal_${withdrawal.type}`}
            message={t(
              `accountDetail_pending_withdrawal_${withdrawal.type}${
                withdrawal.type === 'TARGET' && isNaN(withdrawal.value?.value) ? '_ALL' : ''
              }`,
              {
                title: withdrawal.title || '',
                date: sqlDateToLabel(withdrawal.date, 'dd. MM. yyyy'),
                value: withdrawal.value ? formatAmountValue(withdrawal.value.value, withdrawal.value.currency) : '',
              },
            )}
            variant={MessageVariant.INFO}
            permanent
          />
        ))}

      {pendingTransfers && pendingTransfers.length > 0 && (
        <Message
          key={`account_pendingTransfer_target`}
          id={`account_pendingTransfer_target`}
          message={t('accountDetail_pending_transfer', {
            date: pendingTransfers[0].date,
          })}
          variant={MessageVariant.INFO}
          permanent
        />
      )}

      {store.processing && store.types !== '' && !userMode && (
        <>
          <Message
            id="account_processing"
            message={t('accountDetail_pending_documents')}
            variant={MessageVariant.INFO}
            permanent
          />
          <AccountComplete detailMode={true} accountDetailStore={store} />
        </>
      )}

      {userMode &&
        store.data.detail?.pendingRequestSignature.includes(Request.CONTRACT_AMENDMENT) &&
        store.taxMode !== TaxMode.PPP &&
        store.currentPage !== DetailPage.SIGNATURE && (
          <Message
            id="client_taxMode_OBH"
            content={
              <span>
                {t('accountDetail_taxMode_OBH_notification')}{' '}
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => store.setPage(DetailPage.SIGNATURE)}
                >
                  {t('accountDetail_taxMode_OBH_notification_link')}
                </span>
              </span>
            }
            variant={MessageVariant.INFO}
            permanent
          />
        )}

      {userMode &&
        rootStore?.stores.userStore.activeAccount?.pendingSignatureChanges === SignatureFlow.PORTFOLIO &&
        store.currentPage !== DetailPage.SIGNATURE && (
          <Message
            id="client_portfolio_changes"
            message={t('accountDetail_portfolio_change_notification')}
            variant={MessageVariant.INFO}
            permanent
            action={() => store.setPage(DetailPage.SIGNATURE)}
            actionText={t('accountDetail_portfolio_change_view')}
          />
        )}
    </div>
  );
};

export default compose(inject('rootStore'), observer)(Notifications);
